import React, { useCallback, useEffect, useState } from 'react';
import { parseUnits } from 'ethers';
import { useAccount, useConfig } from 'wagmi';
import { switchChain } from 'wagmi/actions';
import { toast } from 'sonner';
import { Card, Button } from '@/components/ui';
import {
  USDT,
  Token,
  CONTRACTS,
  USDC,
  ETH,
  JOCX,
  POOL_CONFIG,
} from '@/config/constants';
import { chain } from '@/config/wagmi';
import { ethSteps, usdcSteps, usdtSteps } from '@/config/steps';
import { useFlowBalances } from '@/hooks/useFlowBalances';
import { useFlowIncentives } from '@/hooks/useFlowIncentives';
import { useErc20 } from '@/hooks/useErc20';
import { useUniswapV3Router } from '@/hooks/useUniswapV3Router';
import { usePositionManager } from '@/hooks/usePositionManager';
import { calculateSlippageAmounts, encodePath } from '@/utils/uniswapUtils';
import { parsePMResult, parseSwapResult } from '@/utils/common';
import { getIncentiveKey } from '@/utils/common';
import TokenSelector from './TokenSelector';
import SubmitProgress from './SubmitProgress';
import IncentiveSelector from './IncentiveSelector';
import { IncentiveKey, Step } from '@/types';
import { renderBalance } from '@/utils/render.util';

interface OneClickFlowProps {
  onCompleted?: () => void;
  onIncentiveChange?: (incentive: IncentiveKey | undefined) => void;
}

export default function OneClickFlow({
  onCompleted,
  onIncentiveChange,
}: OneClickFlowProps) {
  const [selectedToken, setSelectedToken] = useState<Token>(USDT);
  const [amount, setAmount] = useState<string>('1000');
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const processNextStep = useCallback(() => {
    setSteps((prevSteps) => {
      // Create a deep copy of the steps array to avoid mutation
      const newSteps = prevSteps.map((step) => ({ ...step }));

      const currentStepIndex = newSteps.findIndex(
        (step) => step.status === 'in-progress'
      );
      if (currentStepIndex !== -1) {
        newSteps[currentStepIndex].status = 'completed';
      }

      const nextStepIndex = newSteps.findIndex(
        (step) => step.status === 'waiting'
      );
      if (nextStepIndex !== -1) {
        newSteps[nextStepIndex].status = 'in-progress';
      }

      return newSteps; // Return new array reference
    });
  }, []);
  const { address, chainId } = useAccount();
  const balances = useFlowBalances();
  const incentives = useFlowIncentives();
  const wagmiConfig = useConfig();

  const { jocxPrice } = incentives.poolData;
  const { usdtBalance, usdcBalance, ethBalance } = balances;
  const currentBalance =
    selectedToken.symbol === 'USDT'
      ? usdtBalance
      : selectedToken.symbol === 'USDC'
      ? usdcBalance
      : ethBalance;
  const { hasEnoughUsdt, hasEnoughUsdc, hasEnoughEth } = balances;

  // Check if primary button should be disabled
  const primaryDisabled =
    !address ||
    !incentives.selectedIncentive ||
    !amount ||
    Number(amount) <= 0 ||
    (selectedToken.symbol === 'USDT' && !hasEnoughUsdt(amount)) ||
    (selectedToken.symbol === 'USDC' && !hasEnoughUsdc(amount)) ||
    (selectedToken.symbol === 'ETH' && !hasEnoughEth(amount));

  // Auto-select token with highest USD value and set max amount
  useEffect(() => {
    if (!usdtBalance && !usdcBalance && !ethBalance) return;

    // ETH price estimate (TODO: fetch real-time price)
    const ETH_PRICE_USD = 3500;

    const balanceValues = [
      { token: USDT, balance: parseFloat(usdtBalance || '0'), usdValue: parseFloat(usdtBalance || '0') },
      { token: USDC, balance: parseFloat(usdcBalance || '0'), usdValue: parseFloat(usdcBalance || '0') },
      { token: ETH, balance: parseFloat(ethBalance || '0'), usdValue: parseFloat(ethBalance || '0') * ETH_PRICE_USD },
    ];

    const highest = balanceValues.reduce((max, current) =>
      current.usdValue > max.usdValue ? current : max
    );

    if (highest.balance > 0) {
      setSelectedToken(highest.token);
      setAmount(highest.balance.toString());
    }
  }, [usdtBalance, usdcBalance, ethBalance]);

  // Notify parent component when incentive changes
  useEffect(() => {
    if (onIncentiveChange) {
      onIncentiveChange(incentives.selectedIncentive);
    }
  }, [incentives.selectedIncentive, onIncentiveChange]);

  const getPlanText = () => {
    const halfAmount = Math.max(0, Number(amount || '0') / 2);
    const estimatedJocx = halfAmount / jocxPrice;
    if (selectedToken.symbol === 'USDT') {
      return `Swap ${halfAmount.toFixed(2)} USDT → ${estimatedJocx.toFixed(
        4
      )} JOCX`;
    } else if (selectedToken.symbol === 'USDC') {
      return `Swap ${halfAmount.toFixed(
        2
      )} USDC → USDT + ${estimatedJocx.toFixed(4)} JOCX`;
    } else {
      return `Swap ${halfAmount.toFixed(6)} ETH → USDT + JOCX`;
    }
  };

  const onSelectToken = (token: Token) => {
    setAmount((prev) => {
      if(isNaN(Number(prev))) {
        return '';
      }
      if(selectedToken.decimals > token.decimals) {
        return Number(prev).toFixed(token.decimals);
      }
      return prev;
    });
    setSelectedToken(token);
  };

  const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = e.target.value
        .replace(/[^0-9.]/g, '') // Removes non-numeric characters or periods
        .replace(/^0+(\d)/, '$1') // Remove leading 0 unless a decimal number
        .replace(/^(\.)/, '0$1') // If it starts with a period, add a leading 0
        .replace(/(\..*?)\./g, '$1') // Only one dot is allowed;
        .replace(new RegExp(`(\\.\\d{${selectedToken.decimals}})\\d+`, 'g'), '$1'); // Allow only up to token.decimal
    setAmount(amount);
  };

  const onMaxClick = () => {
    if (currentBalance) {
      setAmount(currentBalance);
    }
  };

  const usdt = useErc20(USDT.address);
  const usdc = useErc20(USDC.address);
  const jocx = useErc20(JOCX.address);
  const uniswapV3Router = useUniswapV3Router();
  const positionManager = usePositionManager();

  const onSubmit = useCallback(async () => {
    setLoading(true);
    try {
      if (chainId !== chain.id) {
        await switchChain(wagmiConfig, { chainId: chain.id });
      }

      if (selectedToken.symbol === ETH.symbol) {
        const halfAmount = Math.max(0, Number(amount || '0') / 2);
        const halfEthBig = parseUnits(
          halfAmount.toFixed(ETH.decimals),
          ETH.decimals
        );

        // Swap half of the amount to USDT
        setSteps(ethSteps);
        processNextStep();
        const pathEthToUsdt = encodePath(
          [CONTRACTS.WETH, CONTRACTS.USDT_TOKEN],
          [POOL_CONFIG.WETH_USDT_FEE]
        );
        const usdtSwapResult = await uniswapV3Router.exactInput({
          amountIn: halfEthBig,
          amountOutMinimum: BigInt(0),
          path: pathEthToUsdt,
          value: halfEthBig,
        });

        const usdtReceivedAmount = parseSwapResult(usdtSwapResult)!.amount;
        const pathEthToJocx = encodePath(
          [CONTRACTS.WETH, CONTRACTS.USDT_TOKEN, CONTRACTS.JOCX_TOKEN],
          [POOL_CONFIG.WETH_USDT_FEE, POOL_CONFIG.JOCX_USDT_FEE]
        );
        const jocxSwapResult = await uniswapV3Router.exactInput({
          amountIn: halfEthBig,
          amountOutMinimum: BigInt(0),
          path: pathEthToJocx,
          value: halfEthBig,
        });
        const jocxReceivedAmount = parseSwapResult(jocxSwapResult)!.amount;

        // Approve USDT & JOCX to Position Manager
        processNextStep();
        await usdt.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          usdtReceivedAmount
        );
        await jocx.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          jocxReceivedAmount
        );

        // Add Liquidity
        processNextStep();
        const addLiquidityResult = await positionManager.mintUsdtJocxPool({
          amount0Desired: usdtReceivedAmount,
          amount1Desired: jocxReceivedAmount,
        });
        const mintedTokenId = parsePMResult(addLiquidityResult);

        // Stake NFT
        processNextStep();
        await positionManager.safeTransferToStake({
          tokenId: BigInt(mintedTokenId!),
          data: getIncentiveKey(incentives.selectedIncentive),
        });
      } else if (selectedToken.symbol === USDT.symbol) {
        // Approve USDT to Router - Initialize steps with first one in progress
        setSteps(usdtSteps);
        processNextStep();

        const halfAmount = Math.max(0, Number(amount || '0') / 2);
        const halfUsdtBig = parseUnits(
          halfAmount.toFixed(USDT.decimals),
          USDT.decimals
        );
        await usdt.approveIfNeeded(CONTRACTS.UNISWAP_V3_ROUTER, halfUsdtBig);

        // Swap half of the amount to JOCX
        processNextStep();
        const estJocxOut =
          jocxPrice > 0
            ? parseUnits(
                (halfAmount / jocxPrice).toFixed(JOCX.decimals),
                JOCX.decimals
              )
            : BigInt(0);
        const minOut = calculateSlippageAmounts(estJocxOut, 20).min;
        const swapResult = await uniswapV3Router.exactInputSingle({
          amountIn: halfUsdtBig,
          amountOutMinimum: minOut,
          tokenIn: CONTRACTS.USDT_TOKEN,
          tokenOut: CONTRACTS.JOCX_TOKEN,
        });
        const jocxReceivedAmount = parseSwapResult(swapResult)!.amount;

        // Approve USDT & JOCX to Position Manager
        processNextStep();
        await usdt.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          halfUsdtBig
        );
        await jocx.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          jocxReceivedAmount
        );

        // Add Liquidity
        processNextStep();
        const addLiquidityResult = await positionManager.mintUsdtJocxPool({
          amount0Desired: halfUsdtBig,
          amount1Desired: jocxReceivedAmount,
        });
        const mintedTokenId = parsePMResult(addLiquidityResult);

        // Stake NFT
        processNextStep();
        await positionManager.safeTransferToStake({
          tokenId: BigInt(mintedTokenId!),
          data: getIncentiveKey(incentives.selectedIncentive),
        });
      } else if (selectedToken.symbol === USDC.symbol) {
        // Approve USDC to Router - Initialize steps with first one in progress
        setSteps(usdcSteps);
        processNextStep();

        const halfAmount = Math.max(0, Number(amount || '0') / 2);
        const halfUsdcBig = parseUnits(
          halfAmount.toFixed(USDC.decimals),
          USDC.decimals
        );
        await usdc.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_ROUTER,
          parseUnits(amount, USDC.decimals)
        );

        // Swap half of the amount to USDT
        processNextStep();
        const minUsdtOut = calculateSlippageAmounts(halfUsdcBig, 5).min;
        const pathUsdcToUsdt = encodePath(
          [CONTRACTS.USDC_TOKEN, CONTRACTS.USDT_TOKEN],
          [POOL_CONFIG.USDC_USDT_FEE]
        );
        const usdtSwapResult = await uniswapV3Router.exactInput({
          amountIn: halfUsdcBig,
          amountOutMinimum: minUsdtOut,
          path: pathUsdcToUsdt,
        });
        const usdtReceivedAmount = parseSwapResult(usdtSwapResult)!.amount;

        const estJocxOut =
          jocxPrice > 0
            ? parseUnits(
                (halfAmount / jocxPrice).toFixed(JOCX.decimals),
                JOCX.decimals
              )
            : BigInt(0);
        const minJocxOut = calculateSlippageAmounts(estJocxOut, 20).min;
        const pathUsdcToJocx = encodePath(
          [CONTRACTS.USDC_TOKEN, CONTRACTS.USDT_TOKEN, CONTRACTS.JOCX_TOKEN],
          [POOL_CONFIG.USDC_USDT_FEE, POOL_CONFIG.JOCX_USDT_FEE]
        );
        const jocxSwapResult = await uniswapV3Router.exactInput({
          amountIn: halfUsdcBig,
          amountOutMinimum: minJocxOut,
          path: pathUsdcToJocx,
        });
        const jocxReceivedAmount = parseSwapResult(jocxSwapResult)!.amount;

        // Approve USDT & JOCX to Position Manager
        processNextStep();
        await usdt.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          usdtReceivedAmount
        );
        await jocx.approveIfNeeded(
          CONTRACTS.UNISWAP_V3_POSITION_MANAGER,
          jocxReceivedAmount
        );

        // Add Liquidity
        processNextStep();
        const addLiquidityResult = await positionManager.mintUsdtJocxPool({
          amount0Desired: usdtReceivedAmount,
          amount1Desired: jocxReceivedAmount,
        });
        const mintedTokenId = parsePMResult(addLiquidityResult);

        // Stake NFT
        processNextStep();
        await positionManager.safeTransferToStake({
          tokenId: BigInt(mintedTokenId!),
          data: getIncentiveKey(incentives.selectedIncentive),
        });
      }

      processNextStep();
      toast.info('Flow executed successfully!');
      onCompleted?.();
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed the flow. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    chainId,
    wagmiConfig,
    selectedToken,
    amount,
    usdt,
    usdc,
    jocx,
    uniswapV3Router,
    positionManager,
    incentives.selectedIncentive,
    jocxPrice,
    processNextStep,
    onCompleted,
  ]);

  return (
    <div className="space-y-6">
      {/* How it works - Brief explanation */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How it works
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full text-slate-600 border border-slate-200">
            <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold">1</span>
            Swap to USDT + JOCX
          </span>
          <span className="text-slate-400">→</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full text-slate-600 border border-slate-200">
            <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold">2</span>
            Provide Liquidity
          </span>
          <span className="text-slate-400">→</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full text-slate-600 border border-slate-200">
            <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold">3</span>
            Stake & Earn JOCX
          </span>
        </div>
      </div>

      {/* Select Incentives - at the top */}
      <IncentiveSelector
        incentives={incentives.createdIncentives}
        selectedIncentive={incentives.selectedIncentive}
        onIncentiveChange={incentives.setSelectedIncentive}
        poolData={incentives.poolData}
      />

      {/* Token Selection and Amount Input */}
      <Card variant="compact" className="p-4">
        <div className="space-y-4">
          {/* Token Selector - Horizontal Buttons with explanation */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-700">
                Select Token
              </label>
              <span className="text-xs text-slate-500">
                LP Pair: USDT / JOCX
              </span>
            </div>
            <TokenSelector
              selectedToken={selectedToken}
              onSelect={onSelectToken}
            />
            {selectedToken.symbol !== 'USDT' && (
              <p className="text-xs text-slate-500">
                {selectedToken.symbol} will be auto-swapped to USDT and JOCX
              </p>
            )}
          </div>

          {/* Amount Input with Transaction Plan */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-700">
                Total Amount ({selectedToken.symbol})
              </label>
              {amount && (
                <span className="text-xs text-blue-600">
                  {getPlanText()}
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount || ''}
                onChange={onAmountChange}
                placeholder={`0.00 ${selectedToken.symbol}`}
                className="w-full px-4 py-3 pr-16 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200 text-lg font-medium"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={onMaxClick}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs px-2 py-1"
              >
                MAX
              </Button>
            </div>

            {/* Balance Display */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-600">
                Balance:{' '}
                {renderBalance(parseUnits((currentBalance || '0'), selectedToken.decimals), { decimals: selectedToken.decimals })}{' '}
                {selectedToken.symbol}
              </span>
              {selectedToken.symbol !== 'ETH' && amount && (
                <span className="text-slate-600">
                  ≈ ${renderBalance(parseUnits(amount, selectedToken.decimals), { decimals: selectedToken.decimals })} USD
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Button
        className="w-full text-lg py-4"
        onClick={onSubmit}
        disabled={primaryDisabled}
        loading={loading}
      >
        {loading ? 'Processing...' : 'Start Earning'}
      </Button>

      {/* Risk notice */}
      <p className="text-xs text-center text-slate-400">
        Your tokens will be converted to LP position and staked automatically.
        <br />
        You can unstake and withdraw anytime from "Manage Position" tab.
      </p>

      {!!steps.length && <SubmitProgress steps={steps} />}
    </div>
  );
}
